import { Check, Loader2, Pencil, Trash2 } from "lucide-react"
import { useEffect, useRef, useState, type ReactNode } from "react"
import { ImageLightboxDialog } from "@/components/image-lightbox-dialog"
import { PlayBadge } from "@/components/media/asset-media-overlay"
import { Button } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { filenameFromUrl } from "@/lib/download-image"
import { SCALE_SHEET_CANVAS_BG } from "@/lib/scale-sheet-layout"
import { cn } from "@/lib/utils"

export type AssetVersionsGalleryItem = {
  id: number
  active: boolean
  preview_url: string | null
  /** Image shown in card / lightbox when different from preview_url (e.g. display_url). */
  display_url?: string | null
  /** Full-resolution URL for lightbox / download when different from preview. */
  download_url?: string | null
  poster_url?: string | null
  origin?: string | null
}

export type AssetVersionsGalleryLabels = {
  active: string
  noPreview: string
  uploaded: string
  generated: string
  cleared: string
  correct: string
  delete: string
  doubleClickActivate: string
  loading: string
  empty: string
  close: string
}

export type AssetVersionsPreviewMode = "image" | "video"
export type AssetVersionsPreviewChrome = "cover" | "contain" | "scaleSheet"

type CardProps<T extends AssetVersionsGalleryItem> = {
  version: T
  busyId: number | null
  previewMode: AssetVersionsPreviewMode
  previewAspectClass: string
  previewChrome: AssetVersionsPreviewChrome
  labels: AssetVersionsGalleryLabels
  originLabel: (version: T) => string
  onActivate: (versionId: number) => void
  onDelete: (versionId: number) => void
  onCorrect?: (version: T) => void
  downloadFilename?: string | null
}

function AssetVersionsGalleryCard<T extends AssetVersionsGalleryItem>({
  version: v,
  busyId,
  previewMode,
  previewAspectClass,
  previewChrome,
  labels,
  originLabel,
  onActivate,
  onDelete,
  onCorrect,
  downloadFilename,
}: CardProps<T>) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const canActivate = !v.active
  const imageUrl = v.display_url ?? v.preview_url
  const lightboxUrl = v.download_url ?? imageUrl
  const isScaleSheet = previewChrome === "scaleSheet"
  const isVideo = previewMode === "video"

  const handleSingleClick = () => {
    if (isVideo ? v.preview_url : lightboxUrl) setLightboxOpen(true)
  }

  const handlePreviewClick = () => {
    if (isVideo ? !v.preview_url : !lightboxUrl) return
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null
      handleSingleClick()
    }, 250)
  }

  const handleActivateDoubleClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current)
      clickTimerRef.current = null
    }
    if (canActivate) onActivate(v.id)
  }

  useEffect(
    () => () => {
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current)
    },
    [],
  )

  return (
    <>
      <div
        className={cn(
          "flex flex-col overflow-hidden rounded-lg border bg-ui-surface-sunken",
          v.active ? "border-emerald-500/50" : "cursor-pointer border-ui-border",
        )}
        onDoubleClick={handleActivateDoubleClick}
        title={canActivate ? labels.doubleClickActivate : undefined}
      >
        <div
          className={cn(
            "relative w-full",
            previewAspectClass,
            !isScaleSheet && "bg-ui-surface-sunken",
            (isVideo ? v.preview_url : imageUrl) && "cursor-zoom-in",
          )}
          style={isScaleSheet && imageUrl ? { backgroundColor: SCALE_SHEET_CANVAS_BG } : undefined}
          onClick={(isVideo ? v.preview_url : imageUrl) ? handlePreviewClick : undefined}
        >
          {imageUrl || (isVideo && v.preview_url) ? (
            isVideo && v.preview_url ? (
              <>
                {v.poster_url ? (
                  <img src={v.poster_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <video
                    src={v.preview_url}
                    className="pointer-events-none h-full w-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-transparent transition-colors hover:bg-black/25">
                  <PlayBadge />
                </div>
              </>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                loading="lazy"
                className={cn(
                  isScaleSheet
                    ? "absolute top-1/2 left-1/2 h-[80%] w-[80%] -translate-x-1/2 -translate-y-1/2 object-contain"
                    : previewChrome === "contain"
                      ? "h-full w-full object-contain"
                      : "h-full w-full object-cover",
                )}
              />
            ) : null
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[11px] text-ui-text-muted">
              {labels.noPreview}
            </div>
          )}
          {v.active && (
            <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-medium text-ui-text-primary">
              <Check className="h-3 w-3" /> {labels.active}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 p-2 text-[11px] text-ui-text-secondary">
          <span className="truncate">{originLabel(v)}</span>
          <div className="flex items-center gap-1">
            {imageUrl && onCorrect ? (
              <Button
                type="button"
                size="icon"
                variant="secondary"
                title={labels.correct}
                aria-label={labels.correct}
                disabled={busyId === v.id}
                onClick={(e) => {
                  e.stopPropagation()
                  onCorrect(v)
                }}
              >
                <Pencil className="h-3 w-3" />
              </Button>
            ) : null}
            <Button
              type="button"
              size="icon"
              variant="ghost"
              title={labels.delete}
              aria-label={labels.delete}
              disabled={busyId === v.id}
              onClick={(e) => {
                e.stopPropagation()
                onDelete(v.id)
              }}
            >
              {busyId === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
      {isVideo ? (
        v.preview_url ? (
          <ImageLightboxDialog
            open={lightboxOpen}
            onOpenChange={setLightboxOpen}
            url={null}
            videoUrl={v.preview_url}
            alt=""
            nested
          />
        ) : null
      ) : (
        <ImageLightboxDialog
          open={lightboxOpen}
          onOpenChange={setLightboxOpen}
          url={lightboxUrl}
          downloadUrl={lightboxUrl}
          downloadFilename={
            downloadFilename && lightboxUrl ? filenameFromUrl(lightboxUrl, downloadFilename) : downloadFilename
          }
          alt=""
          imageBackgroundColor={isScaleSheet ? SCALE_SHEET_CANVAS_BG : undefined}
          imagePadded={isScaleSheet}
        />
      )}
    </>
  )
}

export type AssetVersionsGalleryProps<T extends AssetVersionsGalleryItem> = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  closeAriaLabel: string
  hint?: ReactNode
  toolbar?: ReactNode
  versions: T[]
  loading: boolean
  error: string | null
  busyId: number | null
  labels: AssetVersionsGalleryLabels
  previewMode?: AssetVersionsPreviewMode
  previewAspectClass?: string
  previewChrome?: AssetVersionsPreviewChrome
  originLabel?: (version: T) => string
  onActivate: (versionId: number) => void
  onDelete: (versionId: number) => void
  onCorrect?: (version: T) => void
  downloadFilename?: string | null
}

export function AssetVersionsGallery<T extends AssetVersionsGalleryItem>({
  open,
  onOpenChange,
  title,
  description,
  closeAriaLabel,
  hint,
  toolbar,
  versions,
  loading,
  error,
  busyId,
  labels,
  previewMode = "image",
  previewAspectClass = "aspect-square",
  previewChrome = "cover",
  originLabel,
  onActivate,
  onDelete,
  onCorrect,
  downloadFilename,
}: AssetVersionsGalleryProps<T>) {
  const resolveOrigin =
    originLabel ??
    ((v: T) => {
      if (v.origin === "uploaded") return labels.uploaded
      if (v.origin === "cleared") return labels.cleared
      return labels.generated
    })

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      closeAriaLabel={closeAriaLabel}
      maxHeightClassName="max-h-[92vh]"
      contentClassName="w-[min(96vw,900px)]"
      bodyClassName="flex min-h-0 flex-col"
      footer={
        <Button type="button" variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
          {labels.close}
        </Button>
      }
    >
      {(hint != null || toolbar != null) && (
        <div className="flex items-center justify-between gap-2 border-b border-ui-border p-4">
          <div className="text-xs text-ui-text-secondary">{hint}</div>
          {toolbar}
        </div>
      )}
      <div className="it2tv-scrollbar min-h-0 flex-1 overflow-y-auto p-4">
        {error ? <div className="mb-3 rounded-lg bg-rose-500/10 p-2 text-xs text-ui-status-failed">{error}</div> : null}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-ui-text-secondary">
            <Loader2 className="h-4 w-4 animate-spin" /> {labels.loading}
          </div>
        ) : null}
        {!loading && versions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-ui-border-strong p-6 text-center text-sm text-ui-text-muted dark:bg-black/25 light:bg-clay-2">
            {labels.empty}
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {versions.map((v) => (
            <AssetVersionsGalleryCard
              key={v.id}
              version={v}
              busyId={busyId}
              previewMode={previewMode}
              previewAspectClass={previewAspectClass}
              previewChrome={previewChrome}
              labels={labels}
              originLabel={resolveOrigin}
              onActivate={onActivate}
              onDelete={onDelete}
              onCorrect={onCorrect}
              downloadFilename={downloadFilename}
            />
          ))}
        </div>
      </div>
    </WorkspaceDialog>
  )
}
